import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
    Play,
    Users,
    Trophy,
    Sparkles,
    DollarSign,
    Globe,
    Download,
    ArrowRight
} from 'lucide-react';
import { SEO } from '../components/common/SEO';
import { Button } from '../components/ui/Button';
import { Layout } from '../components/common/Layout';
import { businessInfo, operatorDisclosure } from '../utils/businessInfo';

const features = [
    {
        icon: Sparkles,
        title: 'Express Yourself',
        description: 'Lip-sync to songs, dialogues, and trending audio. Share your creativity with the world.'
    },
    {
        icon: Trophy,
        title: 'Have Fun',
        description: 'Explore memes, comedy skits, and viral challenges. Entertainment at your fingertips.'
    },
    {
        icon: Play,
        title: 'Show Your Moves',
        description: 'Share your best dance and swag videos. Let your talent shine through motion.'
    },
    {
        icon: Users,
        title: 'Shine Like a Star',
        description: 'Use premium filters, beauty effects, and pro editing tools to create stunning content.'
    },
    {
        icon: DollarSign,
        title: 'Creator Rewards',
        description: 'Join contests and access creator reward features where they are currently available.'
    },
    {
        icon: Globe,
        title: 'Growing Community',
        description: 'Connect with creators and audiences across supported regions.'
    }
];

const stats = [
    { value: 'Live', label: 'Android App' },
    { value: 'Soon', label: 'iOS App' },
    { value: 'UGC', label: 'Creator Tools' },
    { value: '24/7', label: 'Safety Support' }
];

const screenshots = [
    { src: '/assets/screenshot/sc_1.webp', alt: 'Swag Shots App Screenshot' },
    { src: '/assets/screenshot/sc_2.webp', alt: 'Swag Shots App Screenshot' },
    { src: '/assets/screenshot/sc_3.webp', alt: 'Swag Shots App Screenshot' },
    { src: '/assets/screenshot/sc_4.webp', alt: 'Swag Shots App Screenshot' }
];

export const HomePage: React.FC = () => {
    const [showIosNotice, setShowIosNotice] = useState(false);

    const showIosComingSoon = () => {
        setShowIosNotice(true);
        window.setTimeout(() => setShowIosNotice(false), 3200);
    };

    useEffect(() => {
        // Smooth scroll behavior for anchor links
        const links = document.querySelectorAll('a[href^="#"]');
        links.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const target = document.querySelector(link.getAttribute('href') || '');
                target?.scrollIntoView({ behavior: 'smooth' });
            });
        });
    }, []);

    return (
        <Layout>
            {showIosNotice && (
                <div
                    role="status"
                    aria-live="polite"
                    className="fixed bottom-6 left-1/2 z-[60] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 rounded-xl bg-gray-950 px-5 py-4 text-center text-sm font-semibold text-white shadow-2xl"
                >
                    iOS app is coming soon. Android is available now.
                </div>
            )}
            <SEO
                title="Swag Shots - Short Video & Live Streaming Platform"
                description="Create short videos, go live, and connect with creators on Swag Shots. Android is available now, with iOS availability coming soon."
                url="https://www.swagshots.com/"
            />

            {/* Hero Section */}
            <section className="relative py-20 md:py-32 bg-gradient-to-br from-primary-50 via-white to-primary-50 overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0">
                    {/* Geometric Background */}
                    <div className="absolute inset-0 opacity-5">
                        <div className="absolute inset-0" style={{
                            backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%2318181b' fill-opacity='0.8'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E\")"
                        }}></div>
                    </div>

                    {/* Floating Elements */}
                    <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-r from-primary-200 to-primary-300 rounded-full opacity-20 animate-pulse"></div>
                    <div className="absolute top-40 right-20 w-16 h-16 bg-gradient-to-r from-primary-300 to-primary-400 rounded-full opacity-30 animate-bounce"></div>
                    <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-gradient-to-r from-primary-400 to-primary-500 rounded-full opacity-25 animate-pulse"></div>
                    <div className="absolute bottom-32 right-1/3 w-24 h-24 bg-gradient-to-r from-primary-100 to-primary-200 rounded-full opacity-20 animate-bounce"></div>

                    {/* Gradient Overlays */}
                    <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-primary-100/30 to-transparent rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-primary-200/20 to-transparent rounded-full blur-3xl"></div>
                </div>

                <div className="container-custom relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        {/* Left Content */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="text-left"
                        >
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.1 }}
                                className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary-100 to-primary-200 text-primary-700 text-sm font-medium mb-6"
                            >
                                <Sparkles className="mr-2" size={16} />
                                Creator Platform
                            </motion.div>

                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-gray-900"
                            >
                                Swag Shots: Create. <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-800">Connect.</span> Earn.
                            </motion.h1>

                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                                className="text-lg md:text-xl mb-8 text-gray-600 leading-relaxed max-w-xl"
                            >
                                Swag Shots is a short-video and live-streaming app built to empower creators and entertain audiences.
                                Showcase your talent, watch trending videos, go live, and explore creator features where available.
                            </motion.p>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.6 }}
                                className="flex flex-col sm:flex-row gap-4"
                            >
                                <Button
                                    size="lg"
                                    className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                                    onClick={() => document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' })}
                                >
                                    <Download className="mr-2" size={20} />
                                    Download Now
                                </Button>
                                <Button
                                    variant="outline"
                                    size="lg"
                                    className="border-primary-300 text-primary-700 hover:bg-primary-50 border-2"
                                    onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                                >
                                    Learn More
                                    <ArrowRight className="ml-2" size={20} />
                                </Button>
                            </motion.div>

                            {/* Stats Preview */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.8 }}
                                className="flex items-center gap-8 mt-12"
                            >
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-gray-900">Live</div>
                                    <div className="text-sm text-gray-500">Android</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-gray-900">Soon</div>
                                    <div className="text-sm text-gray-500">iOS</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-gray-900">UGC</div>
                                    <div className="text-sm text-gray-500">Creators</div>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.9 }}
                                className="mt-8 max-w-xl rounded-lg border border-primary-100 bg-white/80 p-4 text-sm text-gray-600 shadow-sm"
                            >
                                <p>{operatorDisclosure}</p>
                                <p className="mt-1 font-medium text-gray-700">
                                    GSTIN: {businessInfo.gstin}
                                </p>
                            </motion.div>
                        </motion.div>

                        {/* Right Content - App Preview */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="relative"
                        >
                            <div className="relative mx-auto max-w-sm">
                                {/* Phone Frame */}
                                <div className="relative z-10 bg-gradient-to-br from-gray-800 to-gray-900 rounded-[3rem] p-4 shadow-2xl">
                                    <div className="bg-black rounded-[2.5rem] overflow-hidden">
                                        {/* Notch */}
                                        <div className="relative h-8 bg-black">
                                            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-20 h-4 bg-gray-900 rounded-full"></div>
                                        </div>

                                        {/* Screen Content */}
                                        <div className="h-[600px] bg-gradient-to-b from-primary-400 via-primary-500 to-primary-600 relative overflow-hidden">
                                            {/* Mock UI Elements */}
                                            <div className="absolute inset-0 p-4">
                                                <div className="flex justify-between items-center mb-6">
                                                    <div className="w-8 h-8 bg-white/20 rounded-full"></div>
                                                    <div className="text-white font-bold text-lg">Swag Shots</div>
                                                    <div className="w-8 h-8 bg-white/20 rounded-full"></div>
                                                </div>

                                                {/* Video Cards */}
                                                <div className="space-y-4">
                                                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl h-32 p-4 flex items-end">
                                                        <div className="text-white text-sm">Create amazing content...</div>
                                                    </div>
                                                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl h-32 p-4 flex items-end">
                                                        <div className="text-white text-sm">Connect with creators...</div>
                                                    </div>
                                                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl h-32 p-4 flex items-end">
                                                        <div className="text-white text-sm">Explore creator features...</div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Floating Icons */}
                                            <motion.div
                                                animate={{ y: [0, -10, 0] }}
                                                transition={{ duration: 2, repeat: Infinity }}
                                                className="absolute top-20 right-4 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center"
                                            >
                                                <Play size={12} className="text-white" />
                                            </motion.div>
                                            <motion.div
                                                animate={{ y: [0, 10, 0] }}
                                                transition={{ duration: 2.5, repeat: Infinity }}
                                                className="absolute bottom-40 left-4 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center"
                                            >
                                                <Users size={12} className="text-white" />
                                            </motion.div>
                                        </div>
                                    </div>
                                </div>

                                {/* Decorative Elements */}
                                <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-r from-primary-300 to-primary-400 rounded-full opacity-20 animate-pulse"></div>
                                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-r from-primary-400 to-primary-500 rounded-full opacity-30 animate-bounce"></div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-20 md:py-32 bg-gradient-to-b from-gray-50 to-white">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-20"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            viewport={{ once: true }}
                            className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-6"
                        >
                            <Trophy className="mr-2" size={16} />
                            Platform Features
                        </motion.div>

                        <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                            Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-800">Swag Shots</span>?
                        </h2>

                        <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                            Discover the features that make Swag Shots a useful platform for content creators,
                            influencers, and entertainment enthusiasts.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
                        {features.map((feature, index) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -8, scale: 1.02 }}
                                className="group bg-white p-8 lg:p-10 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-primary-200"
                            >
                                <div className="text-primary-600 mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <feature.icon size={56} strokeWidth={1.5} />
                                </div>

                                <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4 leading-tight">
                                    {feature.title}
                                </h3>

                                <p className="text-gray-600 leading-relaxed text-base lg:text-lg">
                                    {feature.description}
                                </p>

                                {/* Decorative gradient line */}
                                <div className="mt-6 w-12 h-1 bg-gradient-to-r from-primary-400 to-primary-600 rounded-full group-hover:w-16 transition-all duration-300"></div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-20 md:py-32 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white relative overflow-hidden">
                {/* Background Elements */}
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-white/10 to-transparent rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-white/5 to-transparent rounded-full blur-3xl"></div>
                </div>

                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-20"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            viewport={{ once: true }}
                            className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-medium mb-6"
                        >
                            <Users className="mr-2" size={16} />
                            Platform Status
                        </motion.div>

                        <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                            Join a Growing <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300">
                                Creative Minds
                            </span>
                        </h2>

                        <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                            Be part of a growing community of content creators, influencers, and entertainment
                            enthusiasts across supported regions.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={stat.label}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: index * 0.15 }}
                                viewport={{ once: true }}
                                whileHover={{ scale: 1.05 }}
                                className="text-center group"
                            >
                                <div className="relative">
                                    <div className="absolute inset-0 bg-white/5 rounded-3xl blur-xl group-hover:bg-white/10 transition-all duration-300"></div>
                                    <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-8 lg:p-10 border border-white/20 group-hover:border-white/30 transition-all duration-300">
                                        <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-b from-white to-white/80">
                                            {stat.value}
                                        </h3>
                                        <p className="text-white/80 text-base lg:text-lg font-medium">
                                            {stat.label}
                                        </p>

                                        {/* Decorative line */}
                                        <div className="mt-4 w-8 h-1 bg-gradient-to-r from-yellow-300 to-orange-300 rounded-full mx-auto group-hover:w-12 transition-all duration-300"></div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Screenshots Section */}
            <section className="py-20 md:py-32 bg-gradient-to-b from-white to-gray-50">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-20"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            viewport={{ once: true }}
                            className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary-100 to-primary-200 text-primary-700 text-sm font-medium mb-6"
                        >
                            <Sparkles className="mr-2" size={16} />
                            App Interface
                        </motion.div>

                        <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                            Experience <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-800">Swag Shots</span>
                        </h2>

                        <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                            Take a peek at our intuitive and beautifully designed interface, crafted specifically
                            for creators to express themselves effortlessly.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                        {screenshots.map((screenshot, index) => (
                            <motion.div
                                key={screenshot.alt}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                whileHover={{ scale: 1.05, y: -8 }}
                                className="group relative"
                            >
                                <div className="relative overflow-hidden rounded-3xl shadow-lg group-hover:shadow-2xl transition-all duration-500">
                                    {/* Background glow effect */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-primary-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                    <img
                                        src={screenshot.src}
                                        alt={screenshot.alt}
                                        className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-110"
                                        loading="lazy"
                                    />

                                    {/* Overlay with title */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end">
                                        <div className="p-6 text-white">
                                            <h3 className="font-semibold text-lg">
                                                Swag Shots
                                            </h3>
                                        </div>
                                    </div>
                                </div>

                                {/* Decorative border */}
                                <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-primary-200 transition-all duration-500"></div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Additional CTA */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                        viewport={{ once: true }}
                        className="text-center mt-16"
                    >
                        <p className="text-gray-600 mb-6 text-lg">
                            Ready to explore these features yourself?
                        </p>
                        <Button
                            size="lg"
                            className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                            onClick={() => document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' })}
                        >
                            <Download className="mr-2" size={20} />
                            Download Swag Shots
                        </Button>
                    </motion.div>
                </div>
            </section>

            {/* Download Section */}
            <section id="download" className="py-20 md:py-32 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white relative overflow-hidden">
                {/* Background Elements */}
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-1/4 w-72 h-72 bg-gradient-to-br from-yellow-400/20 to-orange-400/20 rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-tl from-blue-400/10 to-purple-400/10 rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute inset-0 opacity-20" style={{
                        backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
                    }}></div>
                </div>

                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            viewport={{ once: true }}
                            className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-medium mb-8"
                        >
                            <Download className="mr-2" size={16} />
                            Android Available Now
                        </motion.div>

                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            viewport={{ once: true }}
                            className="text-4xl md:text-6xl font-bold mb-8 leading-tight"
                        >
                            Ready to Get <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300">
                                Started?
                            </span>
                        </motion.h2>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            viewport={{ once: true }}
                            className="text-lg md:text-xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed"
                        >
                            Download Swag Shots on Android today and start sharing stories, talent,
                            and creativity. iOS availability is coming soon.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            viewport={{ once: true }}
                            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
                        >
                            <Button
                                size="lg"
                                className="group bg-white text-primary-600 hover:bg-gray-100 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 px-8 py-4 text-lg font-semibold"
                                onClick={showIosComingSoon}
                            >
                                <Download className="mr-3 group-hover:animate-bounce" size={24} />
                                iOS Coming Soon
                                <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform duration-200" size={20} />
                            </Button>

                            <Button
                                size="lg"
                                className="group bg-white text-primary-600 hover:bg-gray-100 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 px-8 py-4 text-lg font-semibold"
                                onClick={() => window.open('https://play.google.com/store/apps/details?id=com.swagshots.app', '_blank')}
                            >
                                <Download className="mr-3 group-hover:animate-bounce" size={24} />
                                Download for Android
                                <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform duration-200" size={20} />
                            </Button>
                        </motion.div>

                        {/* Additional Info */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                            viewport={{ once: true }}
                            className="mt-12 flex flex-col sm:flex-row gap-8 justify-center items-center text-white/80"
                        >
                            <div className="flex items-center">
                                <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                                <span className="text-sm font-medium">Free to Download</span>
                            </div>
                            <div className="flex items-center">
                                <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                                <span className="text-sm font-medium">No Subscription Required</span>
                            </div>
                            <div className="flex items-center">
                                <div className="w-2 h-2 bg-purple-400 rounded-full mr-3"></div>
                                <span className="text-sm font-medium">Supported Regions</span>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>
        </Layout>
    );
};
